import { z } from 'zod';
/** Behavior event types emitted by the client analytics module.
 *  - session_start: first activity after page load or after a 5-min idle gap.
 *  - page_view:     route change.
 *  - idle_ping:     30s heartbeat while active; activeMs reports real working time.
 *  - interaction:   named UI moment (item_edit_opened, photo_opened, ...).
 *
 *  There is intentionally NO session_end — session boundaries are inferred
 *  server-side from "last activity in session + 5 min idle gap" so we don't
 *  depend on the unreliable beforeunload/pagehide lifecycle.
 */
export const userEventType = z.enum([
    'session_start',
    'page_view',
    'idle_ping',
    'interaction',
]);
/** Shape posted by the client. Server stamps `userId` and `ts` on its own —
 *  any client-supplied values for those are ignored to prevent spoofing. */
export const userEventInput = z.object({
    /** Client wall-clock at the moment the event was captured. */
    clientTs: z.string().datetime({ offset: true }),
    /** Stable for the lifetime of one work session. Minted client-side. */
    sessionId: z.string().min(1).max(120),
    type: userEventType,
    /** Route TEMPLATE (e.g. '/jobs/:jobId#dictation'), not concrete path —
     *  keeps cardinality low so route is groupable. */
    route: z.string().min(1).max(200),
    jobId: z.string().min(1).max(200).optional(),
    targetType: z.string().min(1).max(40).optional(),
    targetId: z.string().min(1).max(200).optional(),
    /** Required for `interaction` events; the open extension point. */
    name: z.string().min(1).max(80).optional(),
    /** Required for `idle_ping`: ms of non-idle activity since the previous ping. */
    activeMs: z.number().int().min(0).max(60_000).optional(),
    meta: z.record(z.unknown()).optional(),
});
/** Batch body for POST /events. Bounded so a runaway client can't blast the
 *  endpoint; bigger flushes should fragment client-side. */
export const userEventBatchInput = z.object({
    events: z.array(userEventInput).min(1).max(50),
});
/** Persisted shape (what lands in /user_events). Server-stamped fields included. */
export const userEvent = userEventInput.extend({
    ts: z.string().datetime({ offset: true }),
    userId: z.string().min(1),
});
